import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { apiClient } from '../../api/client';
import type {
  CreateDepositPayload,
  MarkPaidPayload,
  PublicReceipt,
  ReceiptDetail,
  ReceiptListItem,
  SendReceiptResult,
  UpdateReceiptPayload
} from './types';

interface ReceiptListFilters {
  customerId?: string;
  status?: string;
  kind?: string;
}

// Deposit-balance query keys are ['customer', customerId, 'deposit-balance'].
// Void and draft edits change the balance but only carry the receipt id, so
// invalidate every deposit-balance query by shape.
const isDepositBalanceKey = (queryKey: readonly unknown[]) =>
  queryKey[0] === 'customer' && queryKey[2] === 'deposit-balance';

export function useReceiptList(filters: ReceiptListFilters = {}) {
  return useQuery({
    queryKey: ['receipts', filters],
    queryFn: async () => {
      const res = await apiClient.get<ReceiptListItem[]>('/api/v1/receipts', { params: filters });
      return res.data;
    },
    staleTime: 15_000,
    placeholderData: (prev) => prev
  });
}

export function useReceipt(id: string | undefined) {
  return useQuery({
    queryKey: ['receipt', id],
    queryFn: async () => {
      const res = await apiClient.get<ReceiptDetail>(`/api/v1/receipts/${id}`);
      return res.data;
    },
    enabled: !!id,
    staleTime: 15_000
  });
}

interface CustomerDepositBalance {
  customerId: string;
  depositBalance: number;
}

/** Current deposit credit on a customer's account; shown read-only on the form. */
export function useCustomerDepositBalance(customerId: string | undefined) {
  return useQuery({
    queryKey: ['customer', customerId, 'deposit-balance'],
    queryFn: async () => {
      const res = await apiClient.get<CustomerDepositBalance>(
        `/api/v1/customers/${customerId}/deposit-balance`
      );
      return res.data;
    },
    enabled: !!customerId,
    staleTime: 15_000
  });
}

export function useCreateDeposit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateDepositPayload) => {
      const res = await apiClient.post<ReceiptDetail>('/api/v1/receipts/deposit', payload);
      return res.data;
    },
    onSuccess: (detail) => {
      qc.invalidateQueries({ queryKey: ['receipts'] });
      qc.setQueryData(['receipt', detail.id], detail);
      // Accrual changed the customer's deposit balance — refresh it.
      qc.invalidateQueries({ queryKey: ['customer', detail.customerId, 'deposit-balance'] });
    }
  });
}

export function useUpdateDraft(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateReceiptPayload) => {
      await apiClient.put(`/api/v1/receipts/${id}`, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['receipts'] });
      qc.invalidateQueries({ queryKey: ['receipt', id] });
      qc.invalidateQueries({ predicate: (q) => isDepositBalanceKey(q.queryKey) });
    }
  });
}

export function useSendReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post<SendReceiptResult>(`/api/v1/receipts/${id}/send`);
      return { id, result: res.data };
    },
    onSuccess: ({ id }) => {
      qc.invalidateQueries({ queryKey: ['receipts'] });
      qc.invalidateQueries({ queryKey: ['receipt', id] });
    }
  });
}

export function useMarkPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: MarkPaidPayload }) => {
      await apiClient.post(`/api/v1/receipts/${id}/mark-paid`, payload);
      return id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['receipts'] });
      qc.invalidateQueries({ queryKey: ['receipt', id] });
      // A Final payment changes the customer's account balance.
      qc.invalidateQueries({ predicate: (q) => isDepositBalanceKey(q.queryKey) });
    }
  });
}

export function useVoidReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      notes,
      overridePaidCheck
    }: { id: string; notes?: string; overridePaidCheck?: boolean }) => {
      await apiClient.post(`/api/v1/receipts/${id}/void`, { notes, overridePaidCheck });
      return id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['receipts'] });
      qc.invalidateQueries({ queryKey: ['receipt', id] });
      qc.invalidateQueries({ predicate: (q) => isDepositBalanceKey(q.queryKey) });
    }
  });
}

/** Public receipt fetch — no auth, uses the raw API base URL. */
export function usePublicReceipt(nonce: string | undefined) {
  return useQuery({
    queryKey: ['public-receipt', nonce],
    queryFn: async () => {
      // Separate axios call: the default apiClient attaches a Bearer token via
      // the MSAL ref. The public endpoint must be anonymous.
      const base = import.meta.env.VITE_API_BASE_URL;
      const res = await axios.get<PublicReceipt>(`${base}/api/v1/public/receipts/${nonce}`);
      return res.data;
    },
    enabled: !!nonce,
    staleTime: 60_000,
    retry: false
  });
}
